import {
  ExpandLess,
  ExpandMore,
  ViewColumnOutlined,
  Visibility,
  VisibilityOff,
  VisibilityOutlined,
} from '@mui/icons-material'
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material'
import React, { Fragment, useState } from 'react'

import { ManagedColumnId, TableColumnDefinition } from './constants'

type Props<TColumnId extends string = ManagedColumnId> = {
  columns: TableColumnDefinition<TColumnId>[]
  effectiveHiddenSet: Set<string>
  userHiddenSet: Set<string>
  tenantHiddenSet: Set<string>
  isAdmin: boolean
  disabled?: boolean
  loading?: boolean
  buttonLabel: string
  hiddenForEveryoneLabel: string
  managedByTenantLabel: string
  hiddenForYouLabel: string
  hideForYouLabel: string
  showForYouLabel: string
  hideForEveryoneLabel: string
  showForEveryoneLabel: string
  onHideForUser: (columnId: TColumnId) => Promise<void> | void
  onShowForUser: (columnId: TColumnId) => Promise<void> | void
  onHideForTenant: (columnId: TColumnId) => Promise<void> | void
  onShowForTenant: (columnId: TColumnId) => Promise<void> | void
}

const TableColumnVisibilityMenu = <TColumnId extends string>({
  columns,
  effectiveHiddenSet,
  userHiddenSet,
  tenantHiddenSet,
  isAdmin,
  disabled = false,
  loading = false,
  buttonLabel,
  hiddenForEveryoneLabel,
  managedByTenantLabel,
  hiddenForYouLabel,
  hideForYouLabel,
  showForYouLabel,
  hideForEveryoneLabel,
  showForEveryoneLabel,
  onHideForUser,
  onShowForUser,
  onHideForTenant,
  onShowForTenant,
}: Props<TColumnId>) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [expandedColumnId, setExpandedColumnId] = useState<TColumnId | null>(
    null,
  )

  const handleCloseMenus = () => {
    setMenuAnchorEl(null)
    setExpandedColumnId(null)
  }

  const handleToggleExpanded = (columnId: TColumnId) => {
    setExpandedColumnId(current => (current === columnId ? null : columnId))
  }

  const renderColumnState = (columnId: TColumnId) => {
    if (tenantHiddenSet.has(columnId)) {
      return isAdmin ? hiddenForEveryoneLabel : managedByTenantLabel
    }

    if (userHiddenSet.has(columnId)) {
      return hiddenForYouLabel
    }

    return undefined
  }

  const renderColumnIcon = (columnId: TColumnId) => {
    if (effectiveHiddenSet.has(columnId)) {
      return <VisibilityOff fontSize="small" />
    }

    return <Visibility fontSize="small" />
  }

  return (
    <>
      <Tooltip title={buttonLabel}>
        <span>
          <IconButton
            aria-label={buttonLabel}
            onClick={event => setMenuAnchorEl(event.currentTarget)}
            disabled={disabled}
          >
            <ViewColumnOutlined />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenus}
        PaperProps={{
          sx: {
            width: 292,
            maxWidth: 'calc(100vw - 32px)',
          },
        }}
      >
        {columns.map(column => {
          const isTenantHidden = tenantHiddenSet.has(column.id)
          const isUserHidden = userHiddenSet.has(column.id)

          if (!isAdmin && isTenantHidden) {
            return null
          }

          const isDisabled = disabled || loading || (!isAdmin && isTenantHidden)
          const helperText = renderColumnState(column.id)
          const isExpanded = expandedColumnId === column.id
          const showHideForMe = !isTenantHidden && !isUserHidden
          const showShowForMe = isUserHidden && !isTenantHidden
          const showHideForTenant = isAdmin && !isTenantHidden
          const showShowForTenant = isAdmin && isTenantHidden
          const canExpand =
            !isDisabled &&
            (showHideForMe ||
              showShowForMe ||
              showHideForTenant ||
              showShowForTenant)

          const item = (
            <MenuItem
              key={column.id}
              onClick={() => {
                if (canExpand) {
                  handleToggleExpanded(column.id)
                }
              }}
              disabled={isDisabled && !isTenantHidden}
              sx={{ minWidth: 0, py: 1.25 }}
            >
              <ListItemIcon>
                {column.icon || renderColumnIcon(column.id)}
              </ListItemIcon>
              <ListItemText
                primary={column.label}
                secondary={helperText}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <Stack direction="row" spacing={0.5} alignItems="center">
                {renderColumnIcon(column.id)}
                {canExpand &&
                  (isExpanded ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  ))}
              </Stack>
            </MenuItem>
          )

          return (
            <Fragment key={column.id}>
              {item}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: 1, pb: 1 }}>
                  <Stack spacing={0.5}>
                    {showHideForMe && (
                      <MenuItem
                        disabled={loading}
                        onClick={async () => {
                          await onHideForUser(column.id)
                          handleCloseMenus()
                        }}
                        sx={{ borderRadius: 1, ml: 4 }}
                      >
                        <ListItemIcon>
                          <VisibilityOutlined fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={hideForYouLabel} />
                      </MenuItem>
                    )}
                    {showShowForMe && (
                      <MenuItem
                        disabled={loading}
                        onClick={async () => {
                          await onShowForUser(column.id)
                          handleCloseMenus()
                        }}
                        sx={{ borderRadius: 1, ml: 4 }}
                      >
                        <ListItemIcon>
                          <Visibility fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={showForYouLabel} />
                      </MenuItem>
                    )}
                    {showHideForTenant && (
                      <MenuItem
                        disabled={loading}
                        onClick={async () => {
                          await onHideForTenant(column.id)
                          handleCloseMenus()
                        }}
                        sx={{ borderRadius: 1, ml: 4 }}
                      >
                        <ListItemIcon>
                          <VisibilityOff fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={hideForEveryoneLabel} />
                      </MenuItem>
                    )}
                    {showShowForTenant && (
                      <MenuItem
                        disabled={loading}
                        onClick={async () => {
                          await onShowForTenant(column.id)
                          handleCloseMenus()
                        }}
                        sx={{ borderRadius: 1, ml: 4 }}
                      >
                        <ListItemIcon>
                          <Visibility fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={showForEveryoneLabel} />
                      </MenuItem>
                    )}
                  </Stack>
                </Box>
              </Collapse>
              <Divider />
            </Fragment>
          )
        })}
      </Menu>
    </>
  )
}

export default TableColumnVisibilityMenu
